"use client";

import Link from "next/link";
import { AddToCart } from "@/components/cart/add-to-cart";
import { CheckBox, StarsIcon } from "@/components/icons/assets";

interface ProductInfoProps {
  product: {
    name: string;
    price: string;
    rating: number;
    reviewCount: number;
    shortDescription: string;
    inStock: boolean;
    carFit: {
      model: string;
      plate: string;
    };
    specs: {
      condition: string;
      boltPattern: string;
      frontSpecs: string;
      rearSpecs: string;
    };
  };
  productId: string;
  productStatus: boolean;
  configurableIndex: any[];
  configurableVariants: any[];
}

export default function ProductInfo({ 
  product, 
  productId, 
  productStatus,
  configurableIndex,
  configurableVariants
}: ProductInfoProps) {
  return (
    <div className="xl:h-[629px] flex flex-col justify-between gap-8 xl:gap-0">

      {/* Top Section */}
      <div className="flex flex-col gap-6 xl:gap-10 xl:h-[342px]">

        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 xl:gap-3 xl:h-[30px]">
          <Link
            href="/"
            className="font-oswald font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[100%] tracking-[-0.02em] uppercase text-neutral-300 hover:text-neutral-500"
          >
            Home
          </Link>
          <span className="font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[100%] tracking-[-0.02em] text-neutral-300">/</span>
          <Link
            href="/products"
            className="font-oswald font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[100%] tracking-[-0.02em] capitalize text-neutral-300 hover:text-neutral-500"
          >
            Guni Wheel
          </Link>
          <span className="font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[100%] tracking-[-0.02em] text-neutral-300">/</span>
          <span className="font-oswald font-normal text-[16px] sm:text-[18px] xl:text-[20px] leading-[100%] tracking-[-0.02em] text-neutral-500">
            {product.name}
          </span>
        </nav>

        {/* Name + Stars + Price */}
        <div className="flex flex-col gap-[4px] xl:h-[72px]">
          <h1 className="font-oswald font-medium text-[24px] sm:text-[28px] xl:text-[32px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 xl:h-[47px]">
            {product.name}
          </h1>
          <div className="flex flex-wrap items-center justify-between xl:h-[21px] gap-2">
            <div className="flex items-center gap-2 xl:gap-3">
              <div className="flex items-center gap-2">
                <StarsIcon className="" />
                <span className="font-roboto font-normal text-[12px] sm:text-[14px] leading-[21px] tracking-[0%] text-neutral-300 xl:w-[89px] xl:h-[21px]">
                  ({product.reviewCount} Reviews)
                </span>
              </div>
              <div className="flex items-center gap-3 xl:gap-4">
                <div className="w-px h-4 bg-black opacity-50" />
                <span className={`font-roboto font-medium text-[12px] sm:text-[14px] leading-[21px] ${product.inStock ? 'text-success' : 'text-destructive'}`}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
            </div>
            <span className="font-oswald font-medium text-[20px] sm:text-[22px] xl:text-[26px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 flex-shrink-0">
              {product.price} / WHEEL
            </span>
          </div>
        </div>

        {/* Short Description */}
        <p className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300 w-full xl:h-[50px]">
          {product.shortDescription}
        </p>

        {/* Bullets */}
        <div className="flex flex-col gap-3 xl:gap-5 xl:h-[70px]">
          <div className="flex items-center gap-3 xl:h-[25px]">
            <CheckBox />
            <span className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300">
              Free EU Shipping on orders over $120
            </span>
          </div>
          <div className="flex items-center gap-3 xl:h-[25px]">
            <CheckBox />
            <span className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300">
              2-Year Manufacturer Warranty
            </span>
          </div>
        </div>

      </div>

      {/* Bottom Section */}
      <div className="flex flex-col gap-6 xl:gap-10 xl:h-[199px]">
        <AddToCart
          availableForSale={product.inStock}
          index={configurableIndex}
          productId={productId}
          variants={configurableVariants}
        />
      </div>

    </div>
  );
}